import { BatchOptions, CONCURRENT_TASK_TYPES, UITask } from './types';
import { makeDeleteKeyFromMeta } from './helpers';
import { Runtime } from './runtime';
import { logBatchCompleted, logBatchFailureProgress, logBatchProgress, logger } from './logging';
import { safeEmitBatchProgress } from './progressUtils';
import React from 'react';
import { House } from '@/client';

export type Batch = {
	options: BatchOptions;
	taskIds: string[];
	completedCount: number;
	failedCount: number;
	skippedCount: number;
	cancelledCount: number;
	/** Stopped because an allowance is spent. Finished, so the batch settles, but nothing failed. */
	pausedCount: number;
	duplicateFiles: Map<string, string>;
};

export type BatchManagerDeps = {
	batchesRef: React.MutableRefObject<Map<string, Batch>>;
	globalTasksRef: React.MutableRefObject<Map<string, UITask>>;
	runtime: Runtime;
	processQueuedTasks: () => void;
	house: House;
};

/**
 * Creates batch handlers decoupled from React component body.
 */
export function createBatchManager(deps: BatchManagerDeps) {
	const { batchesRef, globalTasksRef, runtime, processQueuedTasks } = deps;

	const onBatchComplete = async (batchId: string, batch: Batch): Promise<void> => {
		logBatchCompleted(batchId, batch.completedCount, batch.failedCount, batch.skippedCount, batch.cancelledCount);

		// Clean up batch tracking first
		batchesRef.current.delete(batchId);

		// Check if there are any other active batches
		const remainingBatches = Array.from(batchesRef.current.values());
		const hasActiveBatches = remainingBatches.length > 0;

		// Invoke consumer batch callbacks according to outcome (per-batch), regardless of other batches
		try {
			if (batch.failedCount > 0) {
				await batch.options.onFailed?.();
			} else if (batch.completedCount + batch.skippedCount > 0) {
				await batch.options.onComplete?.();
			}
		} catch (err) {
			logger.error(`Error executing batch callbacks for ${batchId}:`, err);
		}

		if (hasActiveBatches) {
			logger.info(
				`Batch ${batchId} completed, but ${remainingBatches.length} other batches are still running. Deferring knowledge base update.`,
			);
			return;
		}
	};

	const onTaskComplete = async (taskId: string, batchId: string): Promise<void> => {
		const batch = batchesRef.current.get(batchId);
		if (!batch) return;

		batch.completedCount++;

		// If this was a delete task, clear its active key
		const uiTask = globalTasksRef.current.get(taskId);
		if (uiTask && uiTask.taskType === CONCURRENT_TASK_TYPES.DELETE_RECORD) {
			const key = makeDeleteKeyFromMeta(uiTask.execution.apiTask.metadata as Record<string, unknown>);
			if (key) runtime.activeDeleteKeys.delete(key);
		}

		// Cleanup dispatched marker and global tracking since task is complete
		runtime.releaseDispatched(taskId);
		globalTasksRef.current.delete(taskId);

		logBatchProgress(taskId, batchId, batch.completedCount, batch.taskIds.length);

		// Check if entire batch is complete
		const totalFinished =
			batch.completedCount + batch.failedCount + batch.skippedCount + batch.cancelledCount + batch.pausedCount;
		if (totalFinished >= batch.taskIds.length) {
			if (totalFinished > batch.taskIds.length) {
				logger.warn(
					`Batch ${batchId} has more finished tasks (${totalFinished}) than total tasks (${batch.taskIds.length}). This should not happen!`,
				);
			}
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished, isFinal: true }, logger);
			await onBatchComplete(batchId, batch);
		} else {
			await safeEmitBatchProgress({ batchId, batch, currentCount: batch.completedCount }, logger);
		}

		// Try to drain queue sooner after a task completes
		setTimeout(() => processQueuedTasks(), 0);
	};

	const onTaskFailed = async (taskId: string, batchId: string): Promise<void> => {
		const batch = batchesRef.current.get(batchId);
		if (!batch) return;

		batch.failedCount++;

		// If this was a delete task, clear its active key
		const uiTask = globalTasksRef.current.get(taskId);
		if (uiTask && uiTask.taskType === CONCURRENT_TASK_TYPES.DELETE_RECORD) {
			const key = makeDeleteKeyFromMeta(uiTask.execution.apiTask.metadata as Record<string, unknown>);
			if (key) runtime.activeDeleteKeys.delete(key);
		}

		// Cleanup dispatched marker and global tracking since task has permanently failed
		runtime.releaseDispatched(taskId);
		globalTasksRef.current.delete(taskId);

		logBatchFailureProgress(taskId, batchId, batch.failedCount, batch.completedCount);

		// Check if entire batch is complete
		const totalFinished =
			batch.completedCount + batch.failedCount + batch.skippedCount + batch.cancelledCount + batch.pausedCount;
		if (totalFinished >= batch.taskIds.length) {
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished, isFinal: true }, logger);
			await onBatchComplete(batchId, batch);
		} else {
			await safeEmitBatchProgress({ batchId, batch, currentCount: batch.failedCount }, logger);
		}
	};

	/**
	 * An upload that stopped because the account's allowance is spent. Terminal so the batch can
	 * settle — a batch that never settles leaves onboarding's continue button disabled forever — but
	 * counted apart from failures, because nothing failed and no retry can help.
	 */
	const onTaskPaused = async (taskId: string, batchId: string): Promise<void> => {
		const batch = batchesRef.current.get(batchId);
		if (!batch) return;

		batch.pausedCount++;
		runtime.releaseDispatched(taskId);
		globalTasksRef.current.delete(taskId);

		const totalFinished =
			batch.completedCount + batch.failedCount + batch.skippedCount + batch.cancelledCount + batch.pausedCount;
		if (totalFinished >= batch.taskIds.length) {
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished, isFinal: true }, logger);
			await onBatchComplete(batchId, batch);
		} else {
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished }, logger);
		}
	};

	const onTaskSkipped = async (taskId: string, batchId: string, duplicateFileName?: string): Promise<void> => {
		const batch = batchesRef.current.get(batchId);
		if (!batch) return;

		batch.skippedCount++;
		if (duplicateFileName) batch.duplicateFiles.set(taskId, duplicateFileName);
		runtime.releaseDispatched(taskId);
		globalTasksRef.current.delete(taskId);

		const totalFinished =
			batch.completedCount + batch.failedCount + batch.skippedCount + batch.cancelledCount + batch.pausedCount;
		if (totalFinished >= batch.taskIds.length) {
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished, isFinal: true }, logger);
			await onBatchComplete(batchId, batch);
		} else {
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished }, logger);
		}

		setTimeout(() => processQueuedTasks(), 0);
	};

	const onTaskCancelled = async (taskId: string, batchId: string): Promise<void> => {
		const batch = batchesRef.current.get(batchId);
		if (!batch) return;

		batch.cancelledCount++;
		const uiTask = globalTasksRef.current.get(taskId);
		if (uiTask && uiTask.taskType === CONCURRENT_TASK_TYPES.DELETE_RECORD) {
			const key = makeDeleteKeyFromMeta(uiTask.execution.apiTask.metadata as Record<string, unknown>);
			if (key) runtime.activeDeleteKeys.delete(key);
		}
		runtime.releaseDispatched(taskId);
		globalTasksRef.current.delete(taskId);

		const totalFinished =
			batch.completedCount + batch.failedCount + batch.skippedCount + batch.cancelledCount + batch.pausedCount;
		if (totalFinished >= batch.taskIds.length) {
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished, isFinal: true }, logger);
			await onBatchComplete(batchId, batch);
		} else {
			await safeEmitBatchProgress({ batchId, batch, currentCount: totalFinished }, logger);
		}

		setTimeout(() => processQueuedTasks(), 0);
	};

	return { onTaskComplete, onTaskFailed, onTaskSkipped, onTaskCancelled, onTaskPaused, onBatchComplete };
}
